import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useGetPaymentMethod,
  useSendPaymentMethodEmail,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { isSubscriptionActiveState } from './utils/smpHelpers';
import { SMPDialogContent, SMPMutationErrorDialog } from './components';
import { SMP_PATH } from '@/constants/paths';

const TRIGGER_EMAIL_METHOD = 'trigger_email';

export const SMPPaymentMethodsPage = () => {
  const navigate = useNavigate();
  const {
    subscriptionDetail,
    queryParams,
    showMaintenance,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const {
    data: paymentMethod,
    isLoading: isLoadingPayment,
  } = useGetPaymentMethod(queryParams, {
    enabled: isSessionReady && !!queryParams.subscriptionId && !!queryParams.customerId,
  });

  const sendEmailMutation = useSendPaymentMethodEmail();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const goBack = () => navigate(SMP_PATH);

  const isLoading = isSMPLoading || isLoadingPayment;

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-80 rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage variant="light" errorType="error" hideBackground hideNavBar />
    );
  }

  const isActive = isSubscriptionActiveState(subscriptionDetail.subscription_status);

  const handleChangePayment = () => {
    if (!paymentMethod) return;

    if (paymentMethod.update_method === TRIGGER_EMAIL_METHOD) {
      sendEmailMutation.mutate(queryParams, {
        onSuccess: () => setShowConfirmDialog(true),
        onError: () => setShowErrorDialog(true),
      });
    } else if (paymentMethod.update_url) {
      window.open(paymentMethod.update_url, '_blank');
    }
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-paymentMethodsPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={goBack}
        />
        <h1 className="typography-h6 text-primary" data-testid="smp-paymentMethods-title">
          Payment Information
        </h1>
      </div>

      {isActive && paymentMethod && (
        <Button
          onClick={handleChangePayment}
          disabled={sendEmailMutation.isPending}
          className="self-start"
          data-testid="smp-paymentMethods-emailBtn"
        >
          {sendEmailMutation.isPending
            ? 'Sending...'
            : 'Email me a link to change payment info'}
        </Button>
      )}

      <Dialog
        open={showConfirmDialog}
        onOpenChange={(isOpen) => !isOpen && setShowConfirmDialog(false)}
      >
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">
              Please check your inbox!
            </DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              For security purposes we sent you an email message with a link to
              update your billing information.
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while sending the payment update email. Please try again."
      />
    </section>
  );
};
